import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, Divider, Form, Input, Modal, Select, Table, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { createUser, fetchRoles, fetchUsers, updateUser } from '../../utils/users';

import { useIsMobile } from '../../hooks/useIsMobile';

const UsersTab = ({ isActive }) => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const [form] = Form.useForm();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const roleLabel = useCallback(
    (role) => {
      const lang = i18n.language;
      if (lang === 'en') return role.role_name_en || role.role_name_zh;
      return role.role_name_zh || role.role_name_en;
    },
    [i18n.language]
  );

  const loadRoles = useCallback(async () => {
    const rolesRes = await fetchRoles();
    setRoles(rolesRes.roles || []);
    return rolesRes.roles || [];
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);

    try {
      const [usersRes, roles] = await Promise.all([fetchUsers(), loadRoles()]);
      setUsers(usersRes.users || []);
      setRoles(roles);
    } catch (err) {
      message.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [loadRoles]);

  useEffect(() => {
    if (!isActive) return;
    loadData();
  }, [isActive, loadData]);

  const buildProfilePayload = (values) => ({
    nameZh: values.nameZh,
    nameEn: values.nameEn || '',
    nameVi: values.nameVi || '',
    nameTh: values.nameTh || '',
    nickname: values.nickname,
    birthday: values.birthday ? values.birthday.format('YYYY-MM-DD') : null,
    gender: values.gender,
    phone: values.phone,
    email: values.email,
    idNumber: values.idNumber,
    countryOfBirth: values.countryOfBirth
  });

  const openCreateModal = async () => {
    try {
      await loadRoles();
    } catch (err) {
      message.error(err.message);
      return;
    }

    setEditingUser(null);
    form.resetFields();
    form.setFieldsValue({ employmentStatus: 'Y' });
    setModalOpen(true);
  };

  const openEditModal = useCallback(
    async (record) => {
      try {
        await loadRoles();
      } catch (err) {
        message.error(err.message);
        return;
      }

      setEditingUser(record);
      form.setFieldsValue({
        userId: record.user_id,
        userRoleId: record.user_role_id,
        employmentStatus: record.employment_status,
        nameZh: record.name_zh,
        nameEn: record.name_en,
        nameVi: record.name_vi,
        nameTh: record.name_th,
        nickname: record.nickname,
        birthday: record.birthday ? dayjs(record.birthday) : null,
        gender: record.gender,
        phone: record.phone,
        email: record.email,
        idNumber: record.id_number,
        countryOfBirth: record.country_of_birth
      });
      setModalOpen(true);
    },
    [form, loadRoles]
  );

  const closeModal = () => {
    setModalOpen(false);
    setEditingUser(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const profile = buildProfilePayload(values);

      if (editingUser) {
        await updateUser(editingUser.user_id, {
          password: values.password || undefined,
          userRoleId: values.userRoleId,
          employmentStatus: values.employmentStatus,
          profile
        });
        message.success(t('users.updateSuccess'));
      } else {
        await createUser({
          userId: values.userId,
          password: values.password,
          userRoleId: values.userRoleId,
          employmentStatus: values.employmentStatus,
          profile
        });
        message.success(t('users.createSuccess'));
      }

      closeModal();
      await loadData();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.message || t('users.saveError'));
    } finally {
      setSubmitting(false);
    }
  };

  const columns = useMemo(
    () => [
      { title: t('users.userId'), dataIndex: 'user_id', key: 'user_id' },
      { title: t('users.nameZh'), dataIndex: 'name_zh', key: 'name_zh' },
      {
        title: t('users.role'),
        key: 'role',
        render: (_, record) => roleLabel(record)
      },
      {
        title: t('users.employmentStatus'),
        dataIndex: 'employment_status',
        key: 'employment_status',
        render: (value) =>
          value === 'Y' ? t('users.employmentActive') : t('users.employmentInactive')
      },
      {
        title: t('users.actions'),
        key: 'actions',
        render: (_, record) => (
          <Button type="link" onClick={() => openEditModal(record)}>
            {t('users.edit')}
          </Button>
        )
      }
    ],
    [openEditModal, roleLabel, t]
  );

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
          {t('users.add')}
        </Button>
      </div>

      <Table
        rowKey="user_id"
        loading={loading}
        columns={columns}
        dataSource={users}
        pagination={{ pageSize: 10, size: isMobile ? 'small' : 'default' }}
        scroll={{ x: 'max-content' }}
        className="system-table"
      />

      <Modal
        title={editingUser ? t('users.editTitle') : t('users.addTitle')}
        open={modalOpen}
        onCancel={closeModal}
        onOk={handleSubmit}
        confirmLoading={submitting}
        destroyOnClose
        width={isMobile ? '100%' : 760}
        className="system-modal"
        styles={{ body: { maxHeight: isMobile ? '60vh' : undefined, overflowY: 'auto' } }}
      >
        <Form form={form} layout="vertical" className="mt-4">
          <Divider orientation="left">{t('users.accountSection')}</Divider>

          <Form.Item
            name="userId"
            label={t('users.userId')}
            rules={[{ required: true, message: t('users.userIdRequired') }]}
          >
            <Input disabled={Boolean(editingUser)} placeholder={t('users.userIdPlaceholder')} />
          </Form.Item>

          <Form.Item
            name="password"
            label={t('users.password')}
            rules={
              editingUser ? [] : [{ required: true, message: t('users.passwordRequired') }]
            }
            extra={editingUser ? t('users.passwordOptional') : undefined}
          >
            <Input.Password placeholder={t('users.passwordPlaceholder')} />
          </Form.Item>

          <Form.Item
            name="userRoleId"
            label={t('users.role')}
            rules={[{ required: true, message: t('users.roleRequired') }]}
          >
            <Select
              placeholder={t('users.rolePlaceholder')}
              options={roles.map((role) => ({
                value: role.role_id,
                label: roleLabel(role)
              }))}
            />
          </Form.Item>

          <Form.Item
            name="employmentStatus"
            label={t('users.employmentStatus')}
            rules={[{ required: true, message: t('users.employmentStatusRequired') }]}
          >
            <Select
              options={[
                { value: 'Y', label: t('users.employmentActive') },
                { value: 'N', label: t('users.employmentInactive') }
              ]}
            />
          </Form.Item>

          <Divider orientation="left">{t('users.profileSection')}</Divider>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
            <Form.Item
              name="nameZh"
              label={t('users.nameZh')}
              rules={[{ required: true, message: t('users.nameZhRequired') }]}
            >
              <Input placeholder={t('users.nameZhPlaceholder')} />
            </Form.Item>

            <Form.Item name="nameEn" label={t('users.nameEn')}>
              <Input placeholder={t('users.nameEnPlaceholder')} />
            </Form.Item>

            <Form.Item name="nameVi" label={t('users.nameVi')}>
              <Input placeholder={t('users.nameViPlaceholder')} />
            </Form.Item>

            <Form.Item name="nameTh" label={t('users.nameTh')}>
              <Input placeholder={t('users.nameThPlaceholder')} />
            </Form.Item>

            <Form.Item name="nickname" label={t('users.nickname')}>
              <Input placeholder={t('users.nicknamePlaceholder')} />
            </Form.Item>

            <Form.Item name="birthday" label={t('users.birthday')}>
              <DatePicker className="w-full" format="YYYY-MM-DD" />
            </Form.Item>

            <Form.Item name="gender" label={t('users.gender')}>
              <Select
                allowClear
                placeholder={t('users.genderPlaceholder')}
                options={[
                  { value: 'M', label: t('users.genderMale') },
                  { value: 'F', label: t('users.genderFemale') },
                  { value: 'N', label: t('users.genderUnknown') }
                ]}
              />
            </Form.Item>

            <Form.Item name="phone" label={t('users.phone')}>
              <Input placeholder={t('users.phonePlaceholder')} />
            </Form.Item>

            <Form.Item name="email" label={t('users.email')}>
              <Input placeholder={t('users.emailPlaceholder')} />
            </Form.Item>

            <Form.Item name="idNumber" label={t('users.idNumber')}>
              <Input placeholder={t('users.idNumberPlaceholder')} />
            </Form.Item>

            <Form.Item name="countryOfBirth" label={t('users.countryOfBirth')}>
              <Input placeholder={t('users.countryOfBirthPlaceholder')} />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default UsersTab;
